/**
 * Cross-subdomain authentication helper
 * Allows login session (user object and auth token) to seamlessly persist
 * across all subdomains (harivishnu.in, blog.harivishnu.in, podcast.harivishnu.in, store.harivishnu.in, apps.harivishnu.in, etc.)
 */

function getAuthCookieDomain(): string {
    if (typeof window === 'undefined') return '';
    const host = window.location.hostname;
    if (host.includes('harivishnu.in')) {
        return '; domain=.harivishnu.in';
    }
    if (host === 'localhost' || host.endsWith('.localhost')) {
        return '; domain=.localhost';
    }
    return '';
}

export function setAuthCookie(key: string, value: string, days?: number): void {
    if (typeof document === 'undefined') return;
    try {
        const domainStr = getAuthCookieDomain();
        const maxAgePart = typeof days === 'number' && days > 0 ? `; max-age=${days * 24 * 60 * 60}` : '';
        const encoded = encodeURIComponent(value);
        const secureFlag = window.location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = `${key}=${encoded}; path=/${domainStr}${maxAgePart}; SameSite=Lax${secureFlag}`;
    } catch (e) {
        console.error("Failed to set auth cookie", e);
    }
}

export function getAuthCookie(key: string): string | null {
    if (typeof document === 'undefined') return null;
    try {
        const match = document.cookie.match(new RegExp('(^|;\\s*)' + key + '=([^;]*)'));
        return match ? decodeURIComponent(match[2]) : null;
    } catch (e) {
        return null;
    }
}

export function removeAuthCookie(key: string): void {
    if (typeof document === 'undefined') return;
    try {
        const domainStr = getAuthCookieDomain();
        document.cookie = `${key}=; path=/${domainStr}; max-age=0; SameSite=Lax`;
        // Also clear host-only cookie just in case
        document.cookie = `${key}=; path=/; max-age=0; SameSite=Lax`;
    } catch (e) {
        console.error("Failed to remove auth cookie", e);
    }
}

export interface AuthUser {
    _id?: string;
    id?: string;
    name?: string;
    email?: string;
    profileImage?: string;
    isSubscribed?: boolean;
    role?: string;
    [key: string]: any;
}

/**
 * Retrieves the current authentication token.
 * Checks sessionStorage first, then cross-subdomain session cookie.
 * Synchronizes to sessionStorage if found in cookie.
 */
export function getAuthToken(): string | null {
    if (typeof window === 'undefined') return null;
    let token = sessionStorage.getItem('token');
    if (!token || token === 'undefined' || token === 'null') {
        token = getAuthCookie('auth_token');
    }
    if (token && token !== 'undefined' && token !== 'null') {
        if (sessionStorage.getItem('token') !== token) {
            try { sessionStorage.setItem('token', token); } catch (_) {}
        }
        return token;
    }
    return null;
}

/**
 * Retrieves the current authenticated user.
 * Checks sessionStorage, then cross-subdomain session cookie.
 */
export function getAuthUser(): AuthUser | null {
    if (typeof window === 'undefined') return null;
    let rawUser = sessionStorage.getItem('user');
    if (!rawUser || rawUser === 'undefined' || rawUser === 'null') {
        rawUser = getAuthCookie('auth_user');
    }

    if (rawUser && rawUser !== 'undefined' && rawUser !== 'null') {
        try {
            const parsed = typeof rawUser === 'string' ? JSON.parse(rawUser) : rawUser;
            if (parsed && typeof parsed === 'object') {
                const serialized = JSON.stringify(parsed);
                if (sessionStorage.getItem('user') !== serialized) {
                    try { sessionStorage.setItem('user', serialized); } catch (_) {}
                }
                return parsed;
            }
        } catch (e) {
            console.error("Failed to parse stored user", e);
        }
    }
    return null;
}

/**
 * Saves auth session (token and user) across sessionStorage and root domain session cookies.
 * Session cookies automatically expire when the browser / session is closed.
 */
export function saveAuthSession(token: string, user: AuthUser): void {
    if (typeof window === 'undefined') return;
    const serializedUser = JSON.stringify(user);

    try {
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('user', serializedUser);
        // Clear any old persistent token/user from localStorage to prevent lingering sessions
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    } catch (_) {}

    // No expiration parameter passed -> creates a session cookie shared across subdomains
    setAuthCookie('auth_token', token);
    setAuthCookie('auth_user', serializedUser);

    // Notify components
    window.dispatchEvent(new CustomEvent('userUpdated', { detail: user }));
}

/**
 * Clears auth session across sessionStorage, localStorage, and root domain cookies.
 */
export function clearAuthSession(): void {
    if (typeof window === 'undefined') return;

    try {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    } catch (_) {}

    removeAuthCookie('auth_token');
    removeAuthCookie('auth_user');

    // Notify components
    window.dispatchEvent(new CustomEvent('userUpdated', { detail: null }));
}

/**
 * Initializes and synchronizes auth state on application startup.
 */
export function initAuthSync(): void {
    if (typeof window === 'undefined') return;
    // Clean up any legacy persistent localStorage auth items from previous logins
    try {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    } catch (_) {}

    const token = getAuthToken();
    const user = getAuthUser();

    if (token && user) {
        // Ensure consistent session cookies
        setAuthCookie('auth_token', token);
        setAuthCookie('auth_user', JSON.stringify(user));
    }
}

/**
 * Monthly read articles & free limit tracking
 */
export const FREE_ARTICLES_LIMIT = 5;

function getCurrentMonthKey(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function isNewsletterSubscribed(): boolean {
    if (typeof window === 'undefined') return false;
    const user = getAuthUser();
    if (user && (user.isSubscribed || user.isNewsletterSubscribed)) return true;
    const localSub = localStorage.getItem('newsletter_subscribed') || getAuthCookie('newsletter_subscribed');
    return localSub === 'true';
}

export function setNewsletterSubscribed(status: boolean = true): void {
    if (typeof window === 'undefined') return;
    try {
        if (status) {
            localStorage.setItem('newsletter_subscribed', 'true');
            setAuthCookie('newsletter_subscribed', 'true', 365);
        } else {
            localStorage.removeItem('newsletter_subscribed');
            removeAuthCookie('newsletter_subscribed');
        }
        window.dispatchEvent(new CustomEvent('newsletterSubscriptionUpdated', { detail: { isSubscribed: status } }));
    } catch (_) {}
}

export function getMonthlyReadArticles(): string[] {
    if (typeof window === 'undefined') return [];
    try {
        const monthKey = getCurrentMonthKey();
        const storedMonth = localStorage.getItem('monthly_articles_period');
        if (storedMonth !== monthKey) {
            // Reset for new month
            localStorage.setItem('monthly_articles_period', monthKey);
            localStorage.setItem('monthly_read_articles', JSON.stringify([]));
            return [];
        }
        const raw = localStorage.getItem('monthly_read_articles');
        return raw ? JSON.parse(raw) : [];
    } catch (e) {
        return [];
    }
}

export function addMonthlyReadArticle(slug: string): string[] {
    if (typeof window === 'undefined' || !slug) return [];
    try {
        const monthKey = getCurrentMonthKey();
        const storedMonth = localStorage.getItem('monthly_articles_period');
        let articles: string[] = [];

        if (storedMonth === monthKey) {
            const raw = localStorage.getItem('monthly_read_articles');
            articles = raw ? JSON.parse(raw) : [];
        } else {
            localStorage.setItem('monthly_articles_period', monthKey);
        }

        if (!articles.includes(slug)) {
            articles.push(slug);
            localStorage.setItem('monthly_read_articles', JSON.stringify(articles));
            window.dispatchEvent(new CustomEvent('monthlyReadArticlesUpdated', { detail: { articles } }));
        }
        return articles;
    } catch (e) {
        return [];
    }
}

